import { useIntl } from 'react-intl'

function useCruiseFAQ(cruiseLine: App.CruiseLine, ship: App.CruiseShip, cruiseType: App.CruiseType): Array<App.CruiseFAQ> {
  const intl = useIntl()

  const buildVendorInfo = (): Array<string> => {
    const cruiseInfo = cruiseLine.vendorInfo?.filter((info) => info.dataType === 'General')[0]
    return cruiseInfo ? [cruiseInfo.text] : []
  }

  function buildShipInfo(): Array<string> {
    const shipFacilities = ship.shipInfo?.filter((info) => info.dataTypeName === 'General') ?? []
    return shipFacilities.map((info: App.CruiseShipInfo) => `<strong>${info.title}</strong><br />${info.description}`)
  }

  // Rich text handlers that produce HTML strings (rendered via dangerouslySetInnerHTML in CruiseFAQSection)
  const richTextValues = {
    b: (chunks: Array<string>) => `<strong>${chunks.join('')}</strong>`,
    br: () => '<br />',
  }

  const insideCabinDesc = intl.formatMessage(
    {
      id: 'HPXF8MHdLRcffXW3WeAX',
      defaultMessage: '<b>Inside Cabin</b> <br></br> An inside cabin is normally the most affordable option, often without a window.',
      description: 'FAQ cabin description for inside cabin type on ocean cruises',
    },
    richTextValues,
  )

  const oceanviewCabinDesc = intl.formatMessage(
    {
      id: 'iPDF6UZNZ4-GGtzrEJfr',
      defaultMessage: '<b>Oceanview Cabin</b> <br></br> An oceanview cabin, often called an outside cabin, is a room with a port hole or window that looks out to sea, depending on deck and location.',
      description: 'FAQ cabin description for oceanview cabin type on ocean cruises',
    },
    richTextValues,
  )

  const balconyCabinOceanDesc = intl.formatMessage(
    {
      id: '30DhS9MFOFzI_e3BaDOa',
      defaultMessage: '<b>Balcony Cabin</b> <br></br> A balcony cabin is any cruise room with a private verandah, allowing you to take in the beautiful views and fresh sea air.',
      description: 'FAQ cabin description for balcony cabin type on ocean cruises',
    },
    richTextValues,
  )

  const suiteCabinDesc = intl.formatMessage(
    {
      id: 'gLknIbhaAm2PLHelDb2_',
      defaultMessage: '<b>Suite Cabin</b> <br></br> A suite is an especially large room within a cruise ship’s top tier of cabins, often with a wide window and/or private verandah.',
      description: 'FAQ cabin description for suite cabin type in the cruise section',
    },
    richTextValues,
  )

  const windowCabinDesc = intl.formatMessage(
    {
      id: 'O0cnmDFrOvGW8BmwJ9mE',
      defaultMessage: '<b>Window Cabin</b> <br></br> A window cabin is a room with a fixed picture window that looks onto the river and passing scenery, depending on deck and location.',
      description: 'FAQ cabin description for window cabin type on river cruises',
    },
    richTextValues,
  )

  const balconyCabinRiverDesc = intl.formatMessage(
    {
      id: '_f1JKf5y4JiIjMAsZxXQ',
      defaultMessage: '<b>Balcony Cabin</b> <br></br> A balcony cabin is any cruise room with a private verandah, allowing you to take in the beautiful views and fresh air.',
      description: 'FAQ cabin description for balcony cabin type on river cruises',
    },
    richTextValues,
  )

  let content = [
    insideCabinDesc,
    oceanviewCabinDesc,
    balconyCabinOceanDesc,
    suiteCabinDesc,
  ]

  if (cruiseType === 'RIVER') {
    content = [
      windowCabinDesc,
      balconyCabinRiverDesc,
      suiteCabinDesc,
    ]
  }

  const whoIsCruiseLineTitle = intl.formatMessage(
    {
      id: 'UIF0AznZroKVdAEUoaXd',
      defaultMessage: 'Who is {cruiseLineName}?',
      description: 'FAQ section title asking about the cruise line in the cruises section',
    },
    { cruiseLineName: cruiseLine.name },
  )

  const facilitiesTitle = intl.formatMessage({
    id: 'WAlhKi1TvrMTcEaUHTpy',
    defaultMessage: 'What facilities are available on this cruise?',
    description: 'FAQ section title about cruise ship facilities in the cruises section',
  })

  const cabinTypesTitle = intl.formatMessage({
    id: 'AZ79dPZPsQs2FZHtXHU3',
    defaultMessage: 'What are the differences between the cabin types?',
    description: 'FAQ section title about cabin type differences in the cruises section',
  })

  const cabinCapacityTitle = intl.formatMessage({
    id: 'gTfppmM-7UxwnJhRqfy7',
    defaultMessage: 'How many people can fit in a cabin?',
    description: 'FAQ section title about cabin capacity in the cruise cabin selection area',
  })

  const cabinCapacityContent = intl.formatMessage({
    id: 'JtxIFWIlD7FyLKHxmgMb',
    defaultMessage: 'The number of people that can fit in a cabin varies between cabin types. Twin Share cabins can accommodate two guests, unless it is specified that the cabins are solo, while Quad Share cabins can accommodate up to four or five guests, subject to individual cruise line policies.',
    description: 'FAQ answer explaining cabin capacity for different cabin types',
  })

  const whatToBringTitle = intl.formatMessage({
    id: 'pB1sDTLedhIZgcdB4DTq',
    defaultMessage: 'What should I bring on a cruise?',
    description: 'FAQ section title about what to bring on a cruise in the cruises section',
  })

  const bringPassport = intl.formatMessage({
    id: '36PIhQtu-9pcqMranpwh',
    defaultMessage: 'Passport and ID',
    description: 'FAQ item - bring passport and ID on a cruise in the cruise section',
  })

  const bringCash = intl.formatMessage({
    id: 'iga3JRc2suaeHbpy10M9',
    defaultMessage: 'Cash',
    description: 'FAQ item - bring cash on a cruise in the cruise offer or booking section',
  })

  const bringFirstAid = intl.formatMessage({
    id: '6d328PWGBw3zOW6fknNU',
    defaultMessage: 'First aid kit',
    description: 'FAQ item - bring first aid kit on a cruise in the cruise section',
  })

  const bringClothing = intl.formatMessage({
    id: '3zmIRPvw3iZiJGcYgV1U',
    defaultMessage: 'Activity-specific clothing and shoes for facilities on the ship and onshore exploration',
    description: 'FAQ item - bring appropriate clothing on a cruise in the cruises section',
  })

  const bringToiletries = intl.formatMessage({
    id: '2pxKc_Ec8Uhbb00e0hC5',
    defaultMessage: 'Toiletries',
    description: 'FAQ item - bring toiletries on a cruise in the cruise offer or booking section',
  })

  const bringBag = intl.formatMessage({
    id: 'FhV_eXkZcoT8CNTg3xSp',
    defaultMessage: 'Carry-on bag and/or backpack for onshore exploration',
    description: 'FAQ item - bring a bag for onshore exploration on a cruise',
  })

  const vaccinationTitle = intl.formatMessage({
    id: 'MeMh4RZ7ncb6zsHpYZ1V',
    defaultMessage: 'Do I need to be fully vaccinated?',
    description: 'FAQ section title about vaccination requirements for cruises',
  })

  const vaccinationContent = intl.formatMessage({
    id: 'I13Ei18bPXtP32GpBYxC',
    defaultMessage: 'Vaccination requirements vary between individual cruise lines. It is strongly recommended to double check the specific cruise line’s vaccination policy and requirements prior to boarding.',
    description: 'FAQ answer about vaccination requirements for cruises',
  })

  const termsTitle = intl.formatMessage({
    id: 'Szpms_OrAGjzM1z6avtd',
    defaultMessage: 'What are the Terms & Conditions?',
    description: 'FAQ section title about terms and conditions in the cruises section',
  })

  const termsContent = intl.formatMessage(
    {
      id: '07MKx74WxG5mafW2qLfS',
      defaultMessage: 'The terms and conditions can be found <link>here.</link>',
      description: 'FAQ answer with link to cruise terms and conditions',
    },
    {
      link: (chunks: Array<string>) => `<a href="https://luxuryescapes.com/au/booking-policies#cruises" target="_blank" rel="noopener noreferrer">${chunks.join('')}</a>`,
    },
  )

  return [
    {
      title: whoIsCruiseLineTitle,
      content: buildVendorInfo(),
      key: 'The vendor',
    },
    {
      title: facilitiesTitle,
      content: buildShipInfo(),
      key: 'The experience',
    },
    {
      title: cabinTypesTitle,
      content,
      key: 'The differences',
    },
    {
      title: cabinCapacityTitle,
      content: [cabinCapacityContent],
      key: 'The group',
    },
    {
      title: whatToBringTitle,
      content: [
        bringPassport,
        bringCash,
        bringFirstAid,
        bringClothing,
        bringToiletries,
        bringBag,
      ],
      key: 'The belongings',
    },
    {
      title: vaccinationTitle,
      content: [vaccinationContent],
      key: 'The Vaccine',
    },
    {
      title: termsTitle,
      content: [termsContent],
      key: 'The terms',
    },
  ]
}

export default useCruiseFAQ
